import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface EmailTemplate {
  id: string;
  community_id: string;
  template_type: 'email_confirmation' | 'password_reset' | 'welcome' | 'business_registration' | 'business_approval' | 'business_listed' | 'pending_subscription_business_listed' | 'listed_for_free' | 'review_notification' | 'offline_notification' | 'job_proposal_notification' | 'proposal_response_notification' | 'admin_business_pending_approval' | 'subscription_pending_completion' | 'incomplete_business_profile' | 'inactive_community_members';
  subject_template: string;
  html_template: string;
  text_template?: string;
  sender_name: string;
  local_part: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateEmailTemplateData {
  community_id: string;
  template_type: 'email_confirmation' | 'password_reset' | 'welcome' | 'business_registration' | 'business_approval' | 'business_listed' | 'pending_subscription_business_listed' | 'listed_for_free' | 'review_notification' | 'offline_notification' | 'job_proposal_notification' | 'proposal_response_notification' | 'admin_business_pending_approval' | 'subscription_pending_completion' | 'incomplete_business_profile' | 'inactive_community_members';
  subject_template: string;
  html_template: string;
  text_template?: string;
  sender_name: string;
  local_part: string;
  is_active?: boolean;
}

export interface UpdateEmailTemplateData {
  subject_template?: string;
  html_template?: string;
  text_template?: string;
  sender_name?: string;
  local_part?: string;
  is_active?: boolean;
}

export function useEmailTemplates(communityId?: string) {
  return useQuery({
    queryKey: ['email-templates', communityId],
    queryFn: async () => {
      let query = supabase
        .from('email_templates')
        .select('*')
        .order('template_type');

      if (communityId) {
        query = query.eq('community_id', communityId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as EmailTemplate[];
    },
    enabled: !!communityId,
  });
}

export function useEmailTemplate(communityId: string, templateType: string) {
  return useQuery({
    queryKey: ['email-template', communityId, templateType],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('community_id', communityId)
        .eq('template_type', templateType)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as EmailTemplate | null;
    },
    enabled: !!communityId && !!templateType,
  });
}

export function useCreateEmailTemplate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateEmailTemplateData) => {
      const { data: result, error } = await supabase
        .from('email_templates')
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      return result as EmailTemplate;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      queryClient.invalidateQueries({ 
        queryKey: ['email-template', data.community_id, data.template_type] 
      });
      toast({
        title: 'Success',
        description: 'Email template created successfully',
      });
    },
    onError: (error: any) => {
      console.error('Error creating email template:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to create email template',
      });
    },
  });
}

export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateEmailTemplateData & { id: string }) => {
      const { data: result, error } = await supabase
        .from('email_templates')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result as EmailTemplate;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      queryClient.invalidateQueries({ 
        queryKey: ['email-template', data.community_id, data.template_type] 
      });
      toast({
        title: 'Success',
        description: 'Email template updated successfully',
      });
    },
    onError: (error: any) => {
      console.error('Error updating email template:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to update email template',
      });
    },
  });
}

export function useDeleteEmailTemplate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      toast({
        title: 'Success',
        description: 'Email template deleted successfully',
      });
    },
    onError: (error: any) => {
      console.error('Error deleting email template:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to delete email template',
      });
    },
  });
}

// Updated to use send-email function instead of send-welcome-email
export function useSendWelcomeEmail() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      userId, 
      userEmail, 
      communityId, 
      userName 
    }: { 
      userId: string; 
      userEmail: string; 
      communityId: string; 
      userName?: string; 
    }) => {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          email_action_type: 'welcome',
          user_id: userId,
          user_email: userEmail,
          community_id: communityId,
          user_name: userName,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Welcome email sent',
        description: 'Welcome email has been sent successfully',
      });
    },
    onError: (error: any) => {
      console.error('Error sending welcome email:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to send welcome email',
      });
    },
  });
}
